import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Building2, Download as DownloadIcon, Smartphone, Laptop, Apple, Chrome,
  Share2, Package, ScanBarcode, DollarSign, BarChart3, Sparkles, ShieldCheck,
  Copy, ArrowLeft, MessageCircle, Bot, WifiOff, Network, CheckCircle2
} from "lucide-react";

const STEPS = {
  android: [
    { icon: Chrome, t: "افتح الرابط بمتصفح Chrome على هاتفك" },
    { icon: DownloadIcon, t: "اضغط على النقاط الثلاث (⋮) ثم «تثبيت التطبيق»" },
    { icon: Smartphone, t: "ستظهر الأيقونة على شاشة هاتفك — افتحها كأي تطبيق" },
  ],
  ios: [
    { icon: Apple, t: "افتح الرابط بمتصفح Safari (ليس Chrome) على الآيفون" },
    { icon: Share2, t: "اضغط زر «مشاركة» (⇪) ثم اختر «إضافة إلى الشاشة الرئيسية»" },
    { icon: Smartphone, t: "ستظهر الأيقونة على شاشتك — افتحها بملء الشاشة" },
  ],
  windows: [
    { icon: Chrome, t: "افتح الرابط بمتصفح Chrome أو Edge على الحاسوب" },
    { icon: DownloadIcon, t: "اضغط أيقونة «التثبيت» (⊕) في يمين شريط العنوان" },
    { icon: Laptop, t: "سيُثبَّت في قائمة ابدأ — يعمل مع طابعة USB وقارئ الباركود" },
  ],
  mac: [
    { icon: Chrome, t: "افتح الرابط بمتصفح Chrome على الـ Mac" },
    { icon: DownloadIcon, t: "من قائمة Chrome → «تثبيت محاسبتي...» أو أيقونة ⊕" },
    { icon: Laptop, t: "يظهر في Launchpad ويعمل كتطبيق مستقل" },
  ],
};

function detectPlatform() {
  if (typeof navigator === "undefined") return "windows";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Mac OS X/.test(ua)) return "mac";
  return "windows";
}

export default function Download() {
  const [tab, setTab] = useState("android");
  const [windowsLinks, setWindowsLinks] = useState(null);
  const [windowsError, setWindowsError] = useState("");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = origin || "https://orgalifestore.org";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=6&color=1E3A8A&data=${encodeURIComponent(shareUrl)}`;

  useEffect(() => { setTab(detectPlatform()); }, []);
  useEffect(() => {
    let alive = true;
    api.get("/downloads/windows").then(({ data }) => { if (alive) setWindowsLinks(data); }).catch((e) => { if (alive) setWindowsError(formatErr(e) || "لا تملك صلاحية تحميل نسخة Windows"); });
    return () => { alive = false; };
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => toast.success("تم نسخ الرابط"));
  };

  const shareWA = () => {
    const msg = encodeURIComponent(`جرّب تطبيق «محاسبتي» — نظام محاسبة عربي متكامل يعمل على الهاتف والحاسوب:\n${shareUrl}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const features = [
    [Package, "متعدد الشركات", "افصل شركاتك وصلاحيات موظفيك"],
    [ScanBarcode, "باركود سريع", "USB + كاميرا الهاتف"],
    [DollarSign, "متعدد العملات", "SYP · TRY · USD · EUR — تلقائي أو يدوي"],
    [BarChart3, "تقارير احترافية", "PDF و Excel + طباعة 80mm و A4"],
    [Sparkles, "مساعد ذكي", "يجاوب بالعربية وينفّذ أوامر المخزون"],
    [ShieldCheck, "أمان وصلاحيات", "دقيقة لكل موظف"],
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Building2 className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-bold">محاسبتي</div>
              <div className="text-[10px] text-muted-foreground">إدارة الشركات والصيدليات</div>
            </div>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm" className="gap-1" data-testid="go-login-btn">
              <ArrowLeft className="w-4 h-4" /> تسجيل الدخول
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-4">
              🌍 يعمل في جميع دول العالم — بدون قيود
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight">
              ثبّت «محاسبتي»<br/>على هاتفك وحاسوبك
            </h1>
            <p className="text-muted-foreground mt-4 text-base md:text-lg leading-loose">
              نسخة تعمل بدون تحميل من متجر — يكفي ضغطة زر لتظهر الأيقونة على شاشتك. متوافق مع Android · iPhone · Windows · Mac، ويعمل بلا إنترنت بعد التثبيت.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              <Button size="lg" onClick={copy} className="gap-2" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" /> نسخ الرابط
              </Button>
              <Button size="lg" variant="outline" onClick={shareWA} className="gap-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="share-wa-btn">
                <Share2 className="w-4 h-4" /> مشاركة عبر واتساب
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-3 font-mono bg-muted rounded px-3 py-2 inline-block ltr:text-left" dir="ltr">
              {shareUrl}
            </div>
          </div>

          {/* QR */}
          <Card className="p-6 md:p-8 grid place-items-center bg-primary/5 border-primary/20">
            <div className="text-center mb-4">
              <div className="font-heading font-bold text-lg">امسح لتثبيت التطبيق</div>
              <div className="text-xs text-muted-foreground mt-1">وجّه كاميرا الهاتف نحو الباركود</div>
            </div>
            <img src={qrUrl} alt="QR" width="260" height="260" className="rounded-lg bg-white p-3 border border-border shadow-lg" data-testid="download-qr" />
            <div className="text-[10px] text-muted-foreground mt-3 font-mono" dir="ltr">{shareUrl}</div>
          </Card>
        </div>
      </div>

      {/* Windows EXE */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pb-8">
        <Card className="p-5 md:p-7 border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-900">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-emerald-600 text-white grid place-items-center shrink-0">
              <Laptop className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl md:text-2xl font-heading font-bold">نسخة Windows محلية بصيغة EXE</h2>
                <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">للشبكة المحلية</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                حمّل مثبت Windows، اضغط «تثبيت»، وسيُنشئ البرنامج الاختصار ويجهّز الخادم المحلي تلقائيًا. تعمل البيانات على الجهاز دون إنترنت، وتدخل الأجهزة الأخرى عبر نفس الشبكة.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><WifiOff className="w-3.5 h-3.5 text-emerald-700" /> يعمل دون إنترنت</span>
                <span className="inline-flex items-center gap-1"><Network className="w-3.5 h-3.5 text-emerald-700" /> يدعم LAN</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> قاعدة بيانات محلية</span>
              </div>
            </div>
            {windowsLinks ? <>
              <a href={windowsLinks.exe_url} className="shrink-0" data-testid="download-windows-exe-btn">
                <Button size="lg" className="w-full md:w-auto gap-2 bg-emerald-600 hover:bg-emerald-700" asChild>
                  <span><DownloadIcon className="w-5 h-5" /> تنزيل مثبت Windows</span>
                </Button>
              </a>
              <a href={windowsLinks.zip_url} className="shrink-0 text-xs text-emerald-800 underline underline-offset-4" data-testid="download-windows-zip-btn">
                تنزيل ZIP بديل
              </a>
            </> : <div className="text-xs text-muted-foreground max-w-xs">{windowsError || "جارٍ التحقق من صلاحية الشركة..."}</div>}
          </div>
          <div className="mt-4 pt-4 border-t border-emerald-200/70 dark:border-emerald-900 text-xs text-muted-foreground">
            بعد التنزيل: شغّل <code className="font-mono">MuhasabatySetup.exe</code> ← اضغط «تثبيت» ← سيظهر اختصار البرنامج على سطح المكتب ويبدأ التشغيل تلقائيًا.
          </div>
        </Card>
      </div>

      {/* Platform tabs */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <h2 className="text-2xl md:text-3xl font-heading font-bold ms-auto">دليل التثبيت</h2>
          <div className="w-full h-px bg-border my-2 md:hidden"></div>
          <div className="flex gap-1 p-1 bg-muted rounded-lg" data-testid="platform-tabs">
            {[
              ["android", "Android", Smartphone],
              ["ios", "iPhone", Apple],
              ["windows", "Windows", Laptop],
              ["mac", "Mac", Apple],
            ].map(([k, label, Icon]) => (
              <button key={k} onClick={()=>setTab(k)}
                className={`px-3 md:px-4 py-2 rounded-md text-sm font-bold flex items-center gap-1.5 transition ${tab === k ? "bg-primary text-primary-foreground shadow" : "hover:bg-background"}`}
                data-testid={`platform-tab-${k}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {STEPS[tab].map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="p-6 relative overflow-hidden">
                <div className="absolute -top-4 -end-4 text-8xl font-heading font-bold text-primary/5 select-none">{i+1}</div>
                <div className="w-11 h-11 rounded-full bg-primary/10 grid place-items-center mb-3">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="text-xs text-muted-foreground font-bold mb-1">الخطوة {i+1}</div>
                <div className="text-sm md:text-base leading-relaxed relative">{s.t}</div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div className="bg-muted/40 border-t border-border">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold">لماذا محاسبتي؟</h2>
            <p className="text-sm text-muted-foreground mt-2">كل ما تحتاجه تجارتك في تطبيق واحد</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map(([Icon, t, s], i) => (
              <Card key={i} className="p-4 md:p-5 hover:border-primary/50 transition">
                <Icon className="w-5 h-5 text-primary mb-2" />
                <div className="font-bold">{t}</div>
                <div className="text-xs text-muted-foreground mt-1">{s}</div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 text-center">
          <div className="flex justify-center gap-2 items-center mb-3">
            <Bot className="w-5 h-5 text-primary" />
            <div className="font-heading font-bold">هل تحتاج مساعدة؟</div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">تحدّث مع مساعد محاسبتي الذكي على شاشة الدخول — يجاوب فوراً على أي استفسار.</p>
          <Link to="/login">
            <Button variant="outline" className="gap-2" data-testid="open-support-btn">
              <MessageCircle className="w-4 h-4" /> افتح شاشة الدخول للمحادثة
            </Button>
          </Link>
          <div className="text-xs text-muted-foreground mt-6">© محاسبتي — جميع الحقوق محفوظة</div>
        </div>
      </div>
    </div>
  );
}
